<template>
    <div>
        <div class="navigation-group" :class="{collapsed: sidebarCollapsed}" ref="group">
            <div class="parent" :class="{open: isOpen || hover}" @click="parentClicked" ref="parent">
                <span class="name" v-show="!sidebarCollapsed">{{route.displayText}}</span>
                <div class="icon left" :class="{active: sidebarCollapsed && isActive}">
                    <i class="fas fa-fw fa-lg" :class="[route.icon]"/>
                </div>
                <div class="icon right" ref="icon">
                    <i class="fas fa-fw" :class="[isOpen ? 'fa-chevron-up' : 'fa-chevron-down']"/>
                </div>
            </div>

            <ul class="children" ref="children">
              <li
                v-for="(child, index) in visibleChildRoutes"
                :key="index"
              >
                    <router-link :to="{name: child.name, query: urlQuery}">
                      {{child.displayText}}
                      <!-- "back" arrow for analytics embedded sisense dashboards -->
                      <i v-if="route.displayText === 'Analytics' &&
                          $route.name === child.name && child.displayText !== 'Ticket Finder'" @click="refreshDashboardFrame"
                        class="fal fa-arrow-left ml-2"
                      />
                    </router-link>
                </li>
            </ul>
        </div>
      <transition name="slide">
        <div class="float-children" ref="floatChildren">
            <div class="title">{{route.displayText}}</div>
            <hr class="divider light mt-1 mb-0"/>
            <ul>
                <li
                  v-for="(child, index) in visibleChildRoutes"
                  :key="index"
                  @click="hideFloatChildren"
                >
                    <router-link :to="{name: child.name, query: urlQuery}">
                      {{child.displayText}}
                      <i v-if="route.displayText === 'Analytics' &&
                          $route.name === child.name && child.displayText !== 'Ticket Finder'" @click="refreshDashboardFrame"
                        class="fal fa-arrow-left ml-2"
                      />
                    </router-link>
                </li>
            </ul>
        </div>
      </transition>
    </div>
</template>

<script>
import velocity from 'velocity-animate'
import Tether from 'tether'
import { mapActions, mapState } from 'vuex'
import userInfoMixin from '../common/mixins/user-info-mixin'

export default {
  name: 'navigation-group',
  props: ['route'],
  mixins: [userInfoMixin],
  data () {
    return {
      isOpen: false,
      active: false,
      hover: false,
      allReportsDashboardId: '5fc809df280143002e4f7e1b'
    }
  },
  computed: {
    ...mapState('navigation', ['hiddenNavItems']),
    ...mapState('login', ['activeLocation', 'user']),
    visibleChildRoutes () {
      return this.route.childRoutes.filter(it => !this.hiddenNavItems.includes(it.id))
    },
    sidebarCollapsed () {
      return this.$store.state.main.sidebarCollapsed
    },
    isActive () {
      return this.route.childRoutes.find(child => child.name === this.$route.name)
    },
    urlQuery () {
      const query = {
        companyId: this.user.userCompanyId
      }

      if (this.activeLocation) {
        query.locationId = this.activeLocation.id
      }

      return query
    }
  },
  watch: {
    '$route.path': function () {
      this.pathChanged()
    }
  },
  mounted () {
    this.$emit('registerGroup', this)

    this.unwatchCollapsed = this.$store.watch(state => state.main.sidebarCollapsed, this.collapseChanged)

    this.pathChanged()

    if (this.sidebarCollapsed) {
      this.setupFloatChildren()
    }
  },
  beforeDestroy () {
    this.unwatchCollapsed()
    velocity.Utilities.removeData(this.$refs.children)
  },
  methods: {
    ...mapActions('navigation', ['hideNavItem', 'showNavItem']),
    refreshDashboardFrame () {
      const iframe = document.getElementsByClassName('dashboard-iframe')[0]
      if (iframe) {
        iframe.src = iframe.src
      }
    },
    pathChanged () {
      if (this.isActive && !this.isOpen) {
        this.toggle()
      }
      if (this.user.userCompanyId !== this.granburyCompanyId) {
        this.hideNavItem('defaultRoles')
      } else {
        this.showNavItem('defaultRoles')
      }
    },
    parentClicked () {
      if (!this.sidebarCollapsed) {
        this.toggle()
      }
    },
    toggle () {
      if (!this.sidebarCollapsed) {
        velocity(this.$refs.children, this.isOpen ? 'slideUp' : 'slideDown')
      }

      this.isOpen = !this.isOpen
      this.$emit('groupOpened', this.route.id)
    },
    close () {
      if (this.isOpen && this.$refs.children) {
        velocity(this.$refs.children, 'slideUp')
        this.isOpen = false
      }
    },
    open () {
      if (!this.isOpen) {
        velocity(this.$refs.children, 'slideDown')
        this.isOpen = false
      }
    },
    collapseChanged () {
      if (!this.sidebarCollapsed) {
        this.isOpen = false
        this.pathChanged()
        if (this.$refs.icon) {
          setTimeout(() => {
            this.$refs.icon.style.display = 'block'
          }, 250)
        }

        this.tether.destroy()
        this.$refs.group.removeEventListener('mouseover', this.mouseOver)
        window.removeEventListener('mouseover', this.outsideListener)
      } else {
        this.setupFloatChildren()
      }
    },
    setupFloatChildren () {
      this.$refs.group.addEventListener('mouseover', this.mouseOver)
      window.addEventListener('mouseover', this.outsideListener)

      this.$refs.icon.style.display = 'none'
      this.$refs.children.style.display = 'none'

      this.tether = new Tether({
        element: this.$refs.floatChildren,
        constraints: [{
          to: 'window',
          pin: ['top', 'bottom', 'left']
        }],
        target: this.$refs.parent,
        attachment: 'top left',
        targetAttachment: 'top right'
      })
    },
    mouseOver () {
      if (this.hover) {
        return
      }

      this.hover = true
      this.$refs.floatChildren.style.display = 'block'
      this.$refs.floatChildren.scrollTop = 0
      this.tether.position()
    },
    outsideListener (event) {
      if (!this.hover) {
        return
      }

      if (!this.$refs.group.contains(event.target) && !this.$refs.floatChildren.contains(event.target)) {
        this.hideFloatChildren()
      }
    },
    hideFloatChildren () {
      this.hover = false
      this.$refs.floatChildren.style.display = 'none'
    }
  }
}
</script>

<style lang="scss">
    @import "@/assets/scss/variables.scss";

    $linkHeight: 48px;
    $hoverBackground: #3A3A3A;

    .navigation-group {
        .parent, .parent-link {
            height: $linkHeight;
            position: relative;
            cursor: pointer;
            line-height: $linkHeight;

            .name {
                font-size: 16px;
                font-weight: 500;
                margin-left: 50px;
            }

            .icon {
                position: absolute;
                height: $linkHeight;
                top: 0;
                align-items: center;

                &.active {
                    color: $accent;
                }

                &.left {
                    left: 15px;
                }

                &.right {
                    right: 15px;
                }
            }

            &:hover, &.open {
                background: $hoverBackground;
            }
        }

        &.collapsed .parent.hover {
            border: 1px solid #595959;
        }

        .parent-link {
            display: block;
            color: white;
            text-decoration: none;

            &.router-link-active {
                color: $accent;
            }
        }

        &.collapsed .icon.right {
            display: none
        }

        .children {
            list-style: none outside none;
            padding-left: 0;
            display: none;

            li a {
                padding-left: 50px;
                height: $linkHeight;
                line-height: $linkHeight;
                display: block;
                color: white;
                text-decoration: none;
                font-size: 16px;
                font-weight: lighter;

                &:hover {
                    background: $hoverBackground;
                }

                &.router-link-active {
                    color: $accent;
                }
            }
        }
    }

    .float-children {
        border: 1px solid #595959;
        color: white;
        display: none;
        background: $sidebarBackground;
        z-index: 9999;
        max-height: 100%;
        overflow-y: auto;
        .title {
            padding: 10px 15px 6px 10px;
            font-size: 1.1rem;
        }

        ul {
            list-style: none outside none;
            padding-left: 0;
            margin-bottom: 15;

            li a {
                padding-left: 20px;
                padding-right: 20px;
                height: $linkHeight;
                line-height: $linkHeight;
                display: block;
                color: white;
                text-decoration: none;
                font-size: 16px;
                font-weight: lighter;

                &:hover {
                    background: $hoverBackground;
                }

                &.router-link-active {
                    color: $accent;
                }
            }
        }

    }
</style>
